import { Stack, SxProps } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { SurveyBySlugRoot } from "../../../types/SurveyBySlug.types";
import { PhasesList } from "../../Responses/types";
import { getFormData } from "../../../Redux/actions/responseAction";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  setPhaseList,
  setResponseLoader,
  setSelectedPhase,
  setSurveyAssignmentStatus,
} from "../../../Redux/reducers/responseSlice";
import PhaseList from "../../Responses/PhaseList";
import QuestionItemWrapper from "../../Responses/QuestionItemWrapper";
import { handlePhaseStatus } from "../../Responses/utils";
import { mapFormAttemptFields } from "../../Responses/formCompletion";
import { getSurveyAssignmentStatusMeta } from "../../Responses/surveyAssignmentStatus";

const rootResponses: SxProps = {
  width: "100%",
  height: "calc(100vh - 194px)",
  display: "flex",
  p: 1,
  overflow: "auto",
  position: "relative",
  alignItems: "flex-start",
};

const AdminSurvey = () => {
  const { id: studyId, participantId } = useParams();
  const dispatch = useAppDispatch();

  const attemptId = useAppSelector(
    (state) => state.response.surveyAssignmentId
  );
  const surveyAssignmentStatus = useAppSelector(
    (state) => state.response.surveyAssignmentStatus
  );
  const phaseList = useAppSelector((state) => state.response.phaseList);

  const [isLoading, setIsLoading] = useState(false);
  const selectedPhase = useAppSelector((state) => state.response.selectedPhase);
  const [packageName, setPackageName] = useState("");
  const [assignmentMeta, setAssignmentMeta] = useState<{
    invitationExpiresAt?: string | null;
  }>({});

  const packageStatusMeta = getSurveyAssignmentStatusMeta({
    status: surveyAssignmentStatus,
    invitationExpiresAt: assignmentMeta.invitationExpiresAt,
    completedForms: phaseList.reduce(
      (sum, phase) =>
        sum +
        (phase.phaseForms || []).filter((f) => f.status === "completed").length,
      0
    ),
    inprogressForms: phaseList.reduce(
      (sum, phase) =>
        sum +
        (phase.phaseForms || []).filter((f) => f.status === "inprogress")
          .length,
      0
    ),
  });

  const fetchStudyPhases = useCallback(async () => {
    try {
      setIsLoading(true);
      dispatch(setResponseLoader(true));
      if (attemptId && studyId && participantId) {
        const { data } = await http.get<SurveyBySlugRoot>(
          `/study/${studyId}/survey-responses/${attemptId}?participantId=${participantId}`
        );

        setPackageName(data?.data.package.name);
        dispatch(setSurveyAssignmentStatus(data?.data?.status || ""));
        setAssignmentMeta({
          invitationExpiresAt: data?.data?.invitationExpiresAt,
        });

        if (data?.data.package.surveyLinks.length === 0) {
          // Set Reducer Form Loading State to false
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        let phases: PhasesList[] = [];

        data?.data.package.surveyLinks.forEach((link) => {
          if (link.phase) {
            const phaseForms = link.phase.phaseForms
              .map((phaseForm) => ({
                position: phaseForm.position,
                id: phaseForm.form.id,
                name: phaseForm.form.name,
                phaseName: link.phase.name,
                phaseId: link.phase.id,
                ...mapFormAttemptFields(phaseForm.form.formAttempts?.[0]),
              }))
              .sort((a, b) => a.position - b.position);

            const phaseStatus = handlePhaseStatus(phaseForms);

            phases.push({
              id: link.phase.id,
              name: link.phase.name,
              position: link.position,
              phaseForms,
              phaseStatus,
            });
          }
        });

        phases.sort((a, b) => a.position - b.position);

        if (phases.length === 0) {
          // Set Reducer Form Loading State to false
          dispatch(setResponseLoader(false));
          setIsLoading(false);
          return;
        }

        let selectedForm = null;
        if (phases.length > 0) {
          let selPhase = phases?.[0]?.id;

          phases.some((phase) => {
            const selForm = phase.phaseForms.find(
              (pf) => pf.status === "active" || pf.status === "inprogress"
            );
            if (selForm) {
              selPhase = phase.id;
              selectedForm = selForm;
            }
            return !!selForm;
          });
          if (!selectedForm) {
            selectedForm = phases?.[0]?.phaseForms?.[0];
          }

          if (selectedForm) {
            dispatch(getFormData({ studyId, selectedForm, participantId }));
          } else {
            dispatch(setResponseLoader(false));
          }

          dispatch(setSelectedPhase({ phase: selPhase }));
        }

        dispatch(setPhaseList({ list: phases }));
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      dispatch(setResponseLoader(false));
      errorToastMessage(error);
    }
  }, [dispatch, attemptId, studyId, participantId]);

  useEffect(() => {
    fetchStudyPhases();
  }, [fetchStudyPhases]);

  return (
    <Stack direction={"row"} sx={rootResponses}>
      {/* Phases and Forms */}

      <PhaseList
        packageName={packageName}
        packageStatusLabel={
          surveyAssignmentStatus ? packageStatusMeta.label : undefined
        }
        packageStatusColor={packageStatusMeta.color}
        packageStatusBgColor={packageStatusMeta.bgColor}
        isLoading={isLoading}
        selectedPhaseId={selectedPhase}
        currentTab="Surveys"
        showCollapse={true}
      />

      {/* Question List */}
      <QuestionItemWrapper />
    </Stack>
  );
};

export default AdminSurvey;
